import { getFontFamily } from './get-font-family';
import { getFontName } from './get-font-name';

const FUTURA_FONT_FAMILY = 'FuturaPTBook';
const FUTURA_FONT_PATH = 'assets/fonts/futura-PT-book.woff2';
const GOOGLE_FONTS_CSS_URL = 'https://fonts.googleapis.com/css2';
const EXPORTED_FONT_WEIGHTS = '400;600;700';
const WOFF2_MIME_TYPE = 'font/woff2';
const CSS_FONT_URL = /url\((https?:\/\/[^)]+)\)/g;

const embeddedFontFaceCssByFontFamily = new Map<string, Promise<string>>();

export function getEmbeddedFontFaceCss(): Promise<string> {
  const fontFamily = getFontName(getFontFamily());
  const embeddedCss = embeddedFontFaceCssByFontFamily.get(fontFamily);

  if (embeddedCss) {
    return embeddedCss;
  }

  const pendingCss = buildEmbeddedFontFaceCss(fontFamily);
  embeddedFontFaceCssByFontFamily.set(fontFamily, pendingCss);

  return pendingCss;
}

async function buildEmbeddedFontFaceCss(fontFamily: string): Promise<string> {
  try {
    if (fontFamily === FUTURA_FONT_FAMILY) {
      return await buildLocalFontFaceCss(FUTURA_FONT_FAMILY, FUTURA_FONT_PATH);
    }

    return await buildGoogleFontFaceCss(fontFamily);
  } catch {
    embeddedFontFaceCssByFontFamily.delete(fontFamily);

    return '';
  }
}

async function buildLocalFontFaceCss(fontFamily: string, fontPath: string): Promise<string> {
  const dataUri = await fetchFontAsDataUri(fontPath);

  return `@font-face { font-family: '${fontFamily}'; src: url(${dataUri}) format('woff2'); }`;
}

async function buildGoogleFontFaceCss(fontFamily: string): Promise<string> {
  const css = await fetchGoogleFontsCss(fontFamily);
  const fontUrls = [...new Set([...css.matchAll(CSS_FONT_URL)].map((match) => match[1]))];
  const dataUris = await Promise.all(fontUrls.map(fetchFontAsDataUri));

  return fontUrls.reduce(
    (embeddedCss, fontUrl, index) => embeddedCss.split(fontUrl).join(dataUris[index]),
    css,
  );
}

async function fetchGoogleFontsCss(fontFamily: string): Promise<string> {
  const family = fontFamily.replace(/ /g, '+');
  const response = await fetch(
    `${GOOGLE_FONTS_CSS_URL}?family=${family}:wght@${EXPORTED_FONT_WEIGHTS}`,
  );

  if (!response.ok) {
    throw new Error(`Unable to load ${fontFamily} from Google Fonts`);
  }

  return response.text();
}

async function fetchFontAsDataUri(url: string): Promise<string> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Unable to load font file ${url}`);
  }

  const font = await response.blob();

  return readAsDataUri(new Blob([font], { type: WOFF2_MIME_TYPE }));
}

function readAsDataUri(font: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (): void => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);

        return;
      }

      reject(new Error('Unable to encode font file'));
    };

    reader.onerror = (): void => reject(reader.error);

    reader.readAsDataURL(font);
  });
}
